import React, { useState, useRef } from 'react';
import {
  buildBackup,
  downloadBackup,
  parseBackup,
  describeBackup,
  applyBackup,
  hasPreRestoreSnapshot,
  undoRestore,
} from '../utils/backup';
import styles from './BackupModal.module.css';

export default function BackupModal({ onClose, onRestored }) {
  const fileRef = useRef(null);

  const [error,    setError]    = useState(null);
  const [success,  setSuccess]  = useState(null);
  const [pending,  setPending]  = useState(null);   // { backup, summary }
  const [mode,     setMode]     = useState('merge');
  const [canUndo,  setCanUndo]  = useState(hasPreRestoreSnapshot());
  // Restores rewrite localStorage underneath React state (the ingredient DB is
  // read once on mount), so the page has to reload before the UI is trustworthy.
  const [needsReload, setNeedsReload] = useState(false);

  const currentCount = buildBackup().recipeCount;

  const handleDownload = () => {
    setError(null);
    try {
      const backup = downloadBackup();
      setSuccess(
        `Backup downloaded — ${backup.recipeCount} ${backup.recipeCount === 1 ? 'formula' : 'formulas'}. ` +
        `Keep it somewhere outside this browser.`
      );
    } catch (e) {
      setError(`Could not create the backup file: ${e.message}`);
    }
  };

  const handleFile = (e) => {
    const file = e.target.files && e.target.files[0];
    // Allow re-picking the same file later
    e.target.value = '';
    if (!file) return;

    setError(null);
    setSuccess(null);

    const reader = new FileReader();
    reader.onerror = () => setError('Could not read that file.');
    reader.onload = () => {
      try {
        const backup  = parseBackup(String(reader.result));
        const summary = describeBackup(backup);
        setPending({ backup, summary });
      } catch (err) {
        setPending(null);
        setError(err.message);
      }
    };
    reader.readAsText(file);
  };

  const handleApply = () => {
    if (!pending) return;
    setError(null);
    try {
      const result = applyBackup(pending.backup, mode);
      setPending(null);
      setCanUndo(true);
      setNeedsReload(true);
      const parts = [
        mode === 'replace'
          ? `Replaced everything with the backup — ${result.added} ${result.added === 1 ? 'formula' : 'formulas'} restored.`
          : `Restored ${result.added} ${result.added === 1 ? 'formula' : 'formulas'}` +
            (result.skipped ? `, skipped ${result.skipped} already here.` : '.'),
      ];
      if (result.costsRestored) {
        parts.push(`Ingredient costs restored for ${result.costsRestored} ingredients.`);
      }
      if (result.customAdded) {
        parts.push(`Added ${result.customAdded} custom ${result.customAdded === 1 ? 'ingredient' : 'ingredients'}.`);
      }
      setSuccess(parts.join(' '));
      if (onRestored) onRestored();
    } catch (e) {
      setError(`Restore failed: ${e.message}`);
    }
  };

  const handleUndo = () => {
    setError(null);
    try {
      const result = undoRestore();
      setCanUndo(false);
      setNeedsReload(true);
      setSuccess(
        `Undone — back to ${result.restoredRecipes} ${result.restoredRecipes === 1 ? 'formula' : 'formulas'}.`
      );
      if (onRestored) onRestored();
    } catch (e) {
      setError(e.message);
    }
  };

  const exportedAtLabel = pending && pending.summary.exportedAt
    ? new Date(pending.summary.exportedAt).toLocaleString()
    : 'unknown';

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={e => e.stopPropagation()}>

        {/* HEADER */}
        <div className={styles.header}>
          <div>
            <div className={styles.title}>Backup &amp; Restore</div>
            <div className={styles.subtitle}>
              Your formulas live only in this browser — a backup file is the only copy you keep.
            </div>
          </div>
          <button className={styles.closeBtn} onClick={onClose} aria-label="Close">✕</button>
        </div>

        {/* STATUS */}
        {(error || success) && (
          <div className={styles.section}>
            {error   && <div className={styles.error}>{error}</div>}
            {success && <div className={styles.success}>{success}</div>}
            {needsReload && (
              <>
                <div className={styles.hint}>
                  Reload the page to see the restored formulas — the app read its data when it
                  started, so the screen behind this is still showing the old set.
                </div>
                <div className={styles.btnRow}>
                  <button className={styles.btnPrimary} onClick={() => window.location.reload()}>
                    ↻ Reload now
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {/* DOWNLOAD */}
        <div className={styles.section}>
          <div className={styles.sectionTitle}>Save a backup</div>
          <div className={styles.countRow}>
            <span className={styles.countNum}>{currentCount}</span>
            <span className={styles.countLabel}>
              {currentCount === 1 ? 'formula' : 'formulas'} in this browser right now
            </span>
          </div>
          <div className={styles.sectionBody}>
            Downloads one file with every formula, your ingredient database, and your saved
            methods.
          </div>
          <div className={styles.btnRow}>
            <button className={styles.btnPrimary} onClick={handleDownload}>
              ⬇ Download backup
            </button>
          </div>
          <div className={styles.hint}>
            Store it in the Mesa Robles project folder, or anywhere that gets backed up.
            Clearing your browser data erases everything the app has — the file is the only way back.
          </div>
        </div>

        {/* RESTORE */}
        <div className={styles.section}>
          <div className={styles.sectionTitle}>Restore from a backup</div>

          {!pending && (
            <>
              <div className={styles.sectionBody}>
                Pick a <code>linkslab-backup-*.json</code> file. You&rsquo;ll see what it holds
                before anything changes.
              </div>
              <div className={styles.btnRow}>
                <button className={styles.btnSecondary} onClick={() => fileRef.current && fileRef.current.click()}>
                  Choose backup file…
                </button>
                {canUndo && (
                  <button className={styles.btnDanger} onClick={handleUndo}>
                    Undo last restore
                  </button>
                )}
              </div>
            </>
          )}

          <input
            ref={fileRef}
            className={styles.hiddenInput}
            type="file"
            accept="application/json,.json"
            onChange={handleFile}
          />

          {pending && (
            <>
              <div className={styles.preview}>
                <div className={styles.previewRow}>
                  <span className={styles.previewKey}>Backup made</span>
                  <span className={styles.previewVal}>{exportedAtLabel}</span>
                </div>
                <div className={styles.previewRow}>
                  <span className={styles.previewKey}>Formulas in file</span>
                  <span className={styles.previewVal}>{pending.summary.incomingRecipes}</span>
                </div>
                <div className={styles.previewRow}>
                  <span className={styles.previewKey}>Already in this browser</span>
                  <span className={styles.previewVal}>{pending.summary.alreadyPresent}</span>
                </div>
                <div className={styles.previewRow}>
                  <span className={styles.previewKey}>Would be added</span>
                  <span className={styles.previewVal}>{pending.summary.wouldAdd}</span>
                </div>
              </div>

              <div className={styles.modeRow}>
                <button
                  className={`${styles.modeOption} ${mode === 'merge' ? styles.modeOptionOn : ''}`}
                  onClick={() => setMode('merge')}
                >
                  <div>
                    <div className={styles.modeLabel}>Merge — add what&rsquo;s missing</div>
                    <div className={styles.modeDesc}>
                      Keeps everything already here and adds the {pending.summary.wouldAdd} formula
                      {pending.summary.wouldAdd === 1 ? '' : 's'} this browser doesn&rsquo;t have.
                      Nothing is overwritten.
                    </div>
                  </div>
                </button>
                <button
                  className={`${styles.modeOption} ${mode === 'replace' ? styles.modeOptionOn : ''}`}
                  onClick={() => setMode('replace')}
                >
                  <div>
                    <div className={styles.modeLabel}>Replace everything</div>
                    <div className={styles.modeDesc}>
                      Discards the {pending.summary.currentRecipes} formula
                      {pending.summary.currentRecipes === 1 ? '' : 's'} here now and restores the file
                      exactly. Use on a fresh browser.
                    </div>
                  </div>
                </button>
              </div>

              <div className={styles.btnRow}>
                <button className={styles.btnPrimary} onClick={handleApply}>
                  {mode === 'replace' ? 'Replace everything' : `Add ${pending.summary.wouldAdd}`}
                </button>
                <button className={styles.btnSecondary} onClick={() => { setPending(null); setError(null); }}>
                  Cancel
                </button>
              </div>

              <div className={styles.hint}>
                Whichever you pick, the current state is snapshotted first so you can undo it.
              </div>
            </>
          )}
        </div>

      </div>
    </div>
  );
}
